// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'

// Platform feature-toggle stubs.
// Persistence is not yet implemented — these values are local UI state only.
// They will be wired to the backend control settings endpoint when it ships.
interface FeatureFlags {
  allowPatientSelfEval: boolean
  enableHIE: boolean
}

interface ToggleRowProps {
  id: string
  label: string
  hint: string
  checked: boolean
  onChange: (checked: boolean) => void
}

function ToggleRow({ id, label, hint, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-border last:border-0">
      <div className="flex flex-col gap-0.5">
        <label htmlFor={id} className="text-sm font-medium text-foreground cursor-pointer">
          {label}
        </label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {/* Native checkbox styled as a toggle */}
      <input
        type="checkbox"
        id={id}
        role="switch"
        aria-checked={checked}
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 rounded accent-primary cursor-pointer shrink-0"
      />
    </div>
  )
}

export default function SettingsPage() {
  const { t } = useTranslation()

  // Local UI state only — persistence ships with the backend settings endpoint.
  const [flags, setFlags] = useState<FeatureFlags>({
    allowPatientSelfEval: true,
    enableHIE: false,
  })

  function setFlag(key: keyof FeatureFlags, value: boolean) {
    setFlags(prev => ({ ...prev, [key]: value }))
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9]">
        <div className="absolute -bottom-16 -right-16 w-[220px] h-[220px] rounded-full bg-[#02C7C3]/20 blur-3xl pointer-events-none" />
        <div className="relative z-10 px-6 py-6 flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
            <Settings className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white leading-tight">{t('settings.title')}</h1>
        </div>
      </div>

      {/* Feature toggles card */}
      <div className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-0">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-foreground">{t('settings.featureToggles')}</h2>
          <p className="text-xs text-muted-foreground mt-1">{t('settings.featureTogglesNote')}</p>
        </div>

        <ToggleRow
          id="allowPatientSelfEval"
          label={t('settings.allowPatientSelfEval')}
          hint={t('settings.allowPatientSelfEvalHint')}
          checked={flags.allowPatientSelfEval}
          onChange={v => setFlag('allowPatientSelfEval', v)}
        />

        <ToggleRow
          id="enableHIE"
          label={t('settings.enableHIE')}
          hint={t('settings.enableHIEHint')}
          checked={flags.enableHIE}
          onChange={v => setFlag('enableHIE', v)}
        />
      </div>

      {/* Disabled save — persistence not yet available */}
      <div className="flex flex-col gap-2">
        <Button disabled className="self-start">
          {t('settings.saveSettings')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('settings.saveNote')}</p>
      </div>
    </div>
  )
}
